import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { Design, Document } from 'blaze-ui/icons'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { BrandPlan } from 'types'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { getBlogPlatformBrandingWithOnboardingSelection } from './utils'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import Styles from './Icon.module.scss'
import cx from 'classnames'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { getUniqueTargetsByProvider } from 'utils/scheduledPostTargetUtils'

interface IconProps {
  event: AlmanacFileCalendarEvent
  isHovered: boolean
  brandPlan?: BrandPlan | null
}

export const ProviderIcon = ({
  provider,
  size = 15,
  isHovered,
  zIndex,
  borderColor,
  marginLeft = -4,
  isReels,
}: {
  provider: ExternalIntegrationEnum
  size?: number
  isHovered: boolean
  zIndex: number
  borderColor?: string
  marginLeft?: number
  isReels?: boolean
}) => {
  return (
    <div
      className={cx(Styles.platformIcon, {
        [Styles.hovered]: isHovered,
        [Styles.youtube]: provider === ExternalIntegrationEnum.YouTube,
        [Styles.facebook]: provider === ExternalIntegrationEnum.Facebook,
      })}
      style={{ zIndex, borderColor, marginLeft }}
    >
      <PlatformIcon platform={provider} size={size} isBrand isReels={isReels} />
    </div>
  )
}

export const Icon = ({ event, isHovered, brandPlan }: IconProps) => {
  const { asset_type, channel, external_integration_id } = event
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const shopifyEnabled = useFlipperFlag('shopify_integration')
  const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
  const { connectedBlogIntegration } = useBlogIntegrations()
  const scheduledPostTargets = event.scheduled_post_targets || []

  const isScheduledPostsV3 = useIsScheduledPostsV3()

  if (isScheduledPostsV3 && scheduledPostTargets.filter((target) => !!target.provider).length > 0) {
    const uniqueTargets = getUniqueTargetsByProvider(scheduledPostTargets)
    return (
      <div className={Styles.platformIconsContainer}>
        {uniqueTargets.map((target, index) => (
          <ProviderIcon
            key={target.id}
            provider={target.provider}
            size={15}
            isHovered={isHovered}
            zIndex={uniqueTargets.length - index}
          />
        ))}
      </div>
    )
  }

  const blogPlatformBranding = channel
    ? getBlogPlatformBrandingWithOnboardingSelection({
        channel,
        externalIntegrationId: external_integration_id ?? undefined,
        externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
        selectedBlogPlatform: brandPlan?.selected_blog_platform,
        featureFlags: {
          wixEnabled,
          blogPlatformOnboardingBrandingEnabled,
          shopifyEnabled,
        },
      })
    : null

  return (
    <>
      {channel && (
        <ChannelIcon size={15} channel={channel} isTransparentBackground={false} blogPlatform={blogPlatformBranding} />
      )}
      {!channel && asset_type === 'Article' && <Document color="var(--dark-80)" size={15} />}
      {!channel && asset_type === 'Design' && <Design color="var(--dark-80)" size={15} />}
    </>
  )
}
